import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  X,
  ShoppingBag,
  ChefHat,
  Truck,
  Settings,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Star,
  Package
} from 'lucide-react';
import AccountDeletionSuccessModal from '../AccountDeletionSuccessModal/AccountDeletionSuccessModal';
import UserKitchenManager from '../UserKitchenManager/UserKitchenManager';
import { trackAuth, trackAccountManagement, trackError } from '../Utils/analytics';
import '../UserProfile/UserProfile.css';

interface UserProfileProps {
  onLogout: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [error, setError] = useState<string | null>(null);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showAccountDeletionSuccess, setShowAccountDeletionSuccess] = useState(false);
  const [showStatusToast, setShowStatusToast] = useState(false);
  const [toastStatus, setToastStatus] = useState<'online' | 'offline'>('online');

  const handleCloseToast = () => {
    setShowStatusToast(false);
  };

  // User data
  const userEmail = localStorage.getItem('userEmail') || 'user@example.com';

  // Notification system
  const [notifications, setNotifications] = useState<Array<{
    id: string;
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  }>>([]);

  const showNotification = (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => {
    const id = Date.now().toString();
    const notification = { id, type, title, message };
    
    setNotifications(prev => [...prev, notification]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      removeNotification(id);
    }, 5000);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);

    const userId = localStorage.getItem('userid');
    // Show info notification that deletion is in progress
    showNotification('info', 'Deleting Account...', 'Please wait while we process your account deletion request.');

    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken || !userId) {
        setError('Authentication required and user ID not found');
        setIsDeletingAccount(false);
        return;
      }

      const response = await fetch(`https://api.homecookt.com/api/v1/users/deleteuser/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Account deleted successfully
        const data = await response.json();
        console.log('User account deleted successfully:', data);
        setShowAccountDeletionSuccess(true);
        trackAuth('account_deleted');
      } else {
        const errorData = await response.json();
        if (response.status === 404) {
          setError('User account not found or already deleted.');
        } else if (response.status === 500) {
          setError('Server error occurred while deleting account. Please try again later.');
        } else {
          setError(errorData.message || 'Failed to delete account. Please try again.');
        }
        showNotification('error', 'Account Deletion Failed', errorData.message || 'Failed to delete account. Please try again.');
        trackError('account_deletion_failed', 'user_profile');
      }
    } catch (error) {
      console.error('Account deletion error:', error);
      setError('Network error occurred while deleting account. Please check your connection.');
      trackError('account_deletion_network_error', 'user_profile');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleAccountDeletionSuccess = () => {
    // Clear all user data
    localStorage.clear();
    
    // Navigate to landing page
    navigate('/', { replace: true });
    
    // Reload the page to ensure clean state
    window.location.reload();
  };

  // Show account deletion success modal
  if (showAccountDeletionSuccess) {
    return (
      <AccountDeletionSuccessModal 
        onContinue={handleAccountDeletionSuccess}
      />
    );
  }

  return (
    <div className="user-profile-page">
      <div className="user-profile-container">
        {/* Profile Header */}
        <div className="user-profile-header">
          <div className="user-profile-image-section">
            <div className="user-profile-image-wrapper">
              <div className="user-profile-image-placeholder">
                <User size={60} />
              </div>
            </div>
          </div>

          <div className="user-profile-info">
            <div className="user-profile-name-section">
              <h1 className="user-profile-name">Welcome Back!</h1>
              <div className="user-profile-rating">
                <Star size={20} fill="currentColor" />
                <span>4.8</span>
                <span className="user-profile-review-count">(24 reviews)</span>
              </div>
            </div>

            <div className="user-profile-contact">
              <div className="user-profile-contact-item">
                <Mail size={20} />
                <span>{userEmail}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="user-profile-stats">
          <div className="user-profile-stat-card">
            <div className="user-profile-stat-icon orders">
              <ShoppingBag size={24} />
            </div>
            <div className="user-profile-stat-info">
              <span className="user-profile-stat-number">12</span>
              <span className="user-profile-stat-label">Orders Placed</span>
            </div>
          </div>

          <div className="user-profile-stat-card">
            <div className="user-profile-stat-icon cooking">
              <ChefHat size={24} />
            </div>
            <div className="user-profile-stat-info">
              <span className="user-profile-stat-number">8</span>
              <span className="user-profile-stat-label">Meals Cooked</span>
            </div>
          </div>

          <div className="user-profile-stat-card">
            <div className="user-profile-stat-icon delivery">
              <Truck size={24} />
            </div>
            <div className="user-profile-stat-info">
              <span className="user-profile-stat-number">5</span>
              <span className="user-profile-stat-label">Deliveries Made</span>
            </div>
          </div>

          <div className="user-profile-stat-card">
            <div className="user-profile-stat-icon donations">
              <Package size={24} />
            </div>
            <div className="user-profile-stat-info">
              <span className="user-profile-stat-number">3</span>
              <span className="user-profile-stat-label">Food Donated</span>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="user-profile-tabs">
          <div className="user-profile-tabs-container">
            <button 
              className={`user-profile-tab-button ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <User className="user-profile-tab-icon" />
              <span className="user-profile-tab-text">Profile</span>
            </button>
            <button 
              className={`user-profile-tab-button ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              <ShoppingBag className="user-profile-tab-icon" />
              <span className="user-profile-tab-text">Orders</span>
            </button>
            <button 
              className={`user-profile-tab-button ${activeTab === 'kitchen' ? 'active' : ''}`}
              onClick={() => setActiveTab('kitchen')}
            >
              <ChefHat className="user-profile-tab-icon" />
              <span className="user-profile-tab-text">My Kitchen</span>
            </button>
            <button 
              className={`user-profile-tab-button ${activeTab === 'delivery' ? 'active' : ''}`}
              onClick={() => setActiveTab('delivery')}
            >
              <Truck className="user-profile-tab-icon" />
              <span className="user-profile-tab-text">Delivery</span>
            </button>
            <button 
              className={`user-profile-tab-button ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              <Settings className="user-profile-tab-icon" />
              <span className="user-profile-tab-text">Settings</span>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="user-profile-tab-content">
          {activeTab === 'profile' && (
            <>
              <h3>Profile Information</h3>
              <div className="user-profile-bio-section">
                <p>Passionate food lover and home cook sharing delicious meals with the community.</p>
              </div>
            </>
          )}

          {activeTab === 'orders' && (
            <>
              <h3>Order History</h3>
              <div className="user-profile-empty-state">
                <ShoppingBag size={60} />
                <p>No orders yet. Start exploring delicious meals!</p>
              </div>
            </>
          )}

          {activeTab === 'kitchen' && (
            <UserKitchenManager showNotification={showNotification} />
          )}

          {activeTab === 'delivery' && (
            <>
              <h3>Delivery History</h3>
              <div className="user-profile-empty-state">
                <Truck size={60} />
                <p>No deliveries yet. Start delivering to earn money!</p>
              </div>
            </>
          )}

          {activeTab === 'settings' && (
            <>
              <h3>Account Settings</h3>
              
              {/* Delete Account Section */}
              <div className="user-profile-delete-zone">
                <div className="user-profile-delete-header">
                  <div className="user-profile-delete-icon">
                    <Trash2 size={20} />
                  </div>
                  <h4 className="user-profile-delete-title">Delete Account</h4>
                </div>
                
                <p className="user-profile-delete-description">
                  Permanently delete your account and all associated data.
                </p>
                
                <div className="user-profile-delete-warning-box">
                  <AlertTriangle size={16} />
                  <p className="user-profile-delete-warning-text">
                    This action cannot be undone and will remove all your data.
                  </p>
                </div>
                
                <div className="user-profile-delete-actions">
                  <button 
                    onClick={() => setShowDeleteConfirmation(true)}
                    disabled={isDeletingAccount}
                    className="user-profile-delete-btn"
                  >
                    {isDeletingAccount ? (
                      <>
                        <div className="user-profile-delete-spinner"></div>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 size={16} />
                        Delete Account
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="user-profile-confirm-dialog-overlay">
          <div className="user-profile-delete-modal">
            <div className="user-profile-delete-modal-header">
              <div className="user-profile-delete-modal-icon">
                <AlertTriangle size={24} />
              </div>
              <h3 className="user-profile-delete-modal-title">Confirm Account Deletion</h3>
            </div>
            
            <div className="user-profile-delete-modal-content">
              <p className="user-profile-delete-modal-text">
                Are you absolutely sure you want to delete your account? This action is permanent and cannot be undone.
              </p>
              
              <div className="user-profile-delete-consequences-list">
                <h4 className="user-profile-delete-consequences-title">What will be deleted:</h4>
                <div className="user-profile-delete-consequences-items">
                  <div className="user-profile-delete-consequence-item">Profile information</div>
                  <div className="user-profile-delete-consequence-item">Order history</div>
                  <div className="user-profile-delete-consequence-item">Kitchen data</div>
                  <div className="user-profile-delete-consequence-item">Payment methods</div>
                  <div className="user-profile-delete-consequence-item">Reviews & ratings</div>
                  <div className="user-profile-delete-consequence-item">All personal data</div>
                </div>
              </div>
              
              <div className="user-profile-delete-final-warning">
                <p className="user-profile-delete-final-warning-text">
                  ⚠️ This action is PERMANENT and IRREVERSIBLE
                </p>
              </div>
            </div>
            
            <div className="user-profile-delete-modal-actions">
              <button 
                onClick={() => setShowDeleteConfirmation(false)}
                className="user-profile-delete-cancel-btn"
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount}
                className="user-profile-delete-confirm-btn"
              >
                {isDeletingAccount ? (
                  <>
                    <div className="user-profile-delete-spinner"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete Forever
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Container */}
      <div className="notification-container">
        {notifications.map(notification => (
          <div key={notification.id} className={`notification ${notification.type}`}>
            <div className="notification-icon">
              {notification.type === 'success' && <CheckCircle size={24} />}
              {notification.type === 'error' && <AlertTriangle size={24} />}
              {notification.type === 'info' && <Settings size={24} />}
              {notification.type === 'warning' && <AlertTriangle size={24} />}
            </div>
            <div className="notification-content">
              <h4 className="notification-title">{notification.title}</h4>
              <p className="notification-message">{notification.message}</p>
            </div>
            <button 
              className="notification-close"
              onClick={() => removeNotification(notification.id)}
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>

      {/* Kitchen Status Toast Notification */}
      {showStatusToast && (
        <div className={`kitchen-status-toast ${toastStatus}`}>
          <div className={`toast-icon ${toastStatus}`}>
            {toastStatus === 'online' ? '🟢' : '🔴'}
          </div>
          <div className="toast-content">
            <h4 className={`toast-title ${toastStatus}`}>
              Kitchen is now {toastStatus === 'online' ? 'Online' : 'Offline'}
            </h4>
            <p className="toast-message">
              {toastStatus === 'online' 
                ? 'Your kitchen is open and ready to receive orders!' 
                : 'Your kitchen is closed and not accepting new orders.'
              }
            </p>
          </div>
          <button className="toast-close" onClick={handleCloseToast}>
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default UserProfile;